import { useState, ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink } from "lucide-react";
import { isHilerIQUrl } from "@/lib/hileriq";

interface ExternalLinkWarningProps {
  href: string;
  children: ReactNode;
  className?: string;
}

/** Extract a readable site name from a URL */
const getSiteName = (url: string): string => {
  try {
    const hostname = new URL(url).hostname.replace("www.", "");
    return hostname;
  } catch {
    return url;
  }
};

/**
 * Wraps any external link. On click shows a "leaving Hiler Mortgage" disclaimer
 * dialog before opening the external URL in a new tab.
 */
const ExternalLinkWarning = ({ href, children, className }: ExternalLinkWarningProps) => {
  const [open, setOpen] = useState(false);

  // Low-friction destinations (e.g. Google Maps for the office address) bypass the interstitial.
  const isAllowlisted = (() => {
    try {
      const h = new URL(href).hostname.toLowerCase();
      return (
        // HilerIQ is a first-party Hiler Mortgage platform, not a third-party site.
        isHilerIQUrl(href) ||
        h === "maps.google.com" ||
        h === "www.google.com" && new URL(href).pathname.startsWith("/maps") ||
        h === "google.com" && new URL(href).pathname.startsWith("/maps") ||
        h === "goo.gl" ||
        h === "maps.app.goo.gl"
      );
    } catch {
      return false;
    }
  })();

  if (isAllowlisted) {
    return (
      <a href={href} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  const siteName = getSiteName(href);

  return (
    <>
      <a
        href={href}
        className={className}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClick(e);
        }}
      >
        {children}
      </a>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" />
              You're Leaving Hiler Mortgage
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-base leading-relaxed text-muted-foreground">
                  You are about to visit an external website. Hiler Mortgage is not responsible for the content, privacy practices, security, or accessibility of external sites.
                </p>
                <p className="text-sm font-medium text-foreground break-all">
                  Destination: {siteName}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on Hiler Mortgage</AlertDialogCancel>
            <AlertDialogAction asChild>
              <a href={href} target="_blank" rel="noopener noreferrer">
                Continue to External Site
              </a>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExternalLinkWarning;
